import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Button,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Sidebar from "../Sidebar/Sidebar";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import MenuItem from "@mui/material/MenuItem";
import config from "../../config";

const authHeaders = () => {
  const token = sessionStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatQuantity = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("en-IN");
};

const SEASON_OPTIONS = [
  { value: "SEASON_1", label: "Season 1" },
  { value: "SEASON_2", label: "Season 2" },
  { value: "SEASON_3", label: "Season 3" },
  { value: "SEASON_4", label: "Season 4" },
];
const SEASON_ORDER = SEASON_OPTIONS.reduce((acc, s, idx) => {
  acc[s.value] = idx;
  return acc;
}, {});
const seasonLabel = (value) =>
  SEASON_OPTIONS.find((s) => s.value === value)?.label || value || "—";

const currentYear = new Date().getFullYear();

const checkboxIcon = <CheckBoxOutlineBlankRoundedIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxRoundedIcon fontSize="small" />;

export default function VarietyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [variety, setVariety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editVarietyDialogOpen, setEditVarietyDialogOpen] = useState(false);
  const [editVarietyName, setEditVarietyName] = useState("");
  const [editVarietyCode, setEditVarietyCode] = useState("");
  const [editVarietyDescription, setEditVarietyDescription] = useState("");
  const [editVarietyStatus, setEditVarietyStatus] = useState(true);
  const [editVarietyError, setEditVarietyError] = useState("");
  const [savingVariety, setSavingVariety] = useState(false);

  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [allBuyers, setAllBuyers] = useState([]);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [buyersError, setBuyersError] = useState("");
  const [selectedBuyers, setSelectedBuyers] = useState([]);
  const [mapping, setMapping] = useState(false);
  const [removeDialogBuyer, setRemoveDialogBuyer] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [arrivalDialogOpen, setArrivalDialogOpen] = useState(false);
  const [arrivalYear, setArrivalYear] = useState(String(currentYear));
  const [arrivalSeason, setArrivalSeason] = useState("");
  const [arrivalQuantity, setArrivalQuantity] = useState("");
  const [arrivalMandiLocation, setArrivalMandiLocation] = useState("");
  const [arrivalMandiPartner, setArrivalMandiPartner] = useState(null);
  const [arrivalFormError, setArrivalFormError] = useState("");
  const [savingArrival, setSavingArrival] = useState(false);
  const [removeArrivalTarget, setRemoveArrivalTarget] = useState(null);
  const [removingArrivalKey, setRemovingArrivalKey] = useState(null);

  const [mandiPartners, setMandiPartners] = useState([]);
  const [mandiPartnersLoaded, setMandiPartnersLoaded] = useState(false);
  const [mandiPartnersLoading, setMandiPartnersLoading] = useState(false);
  const [mandiPartnersError, setMandiPartnersError] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchVariety = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${config.apiBaseUrl}/variety/get/${id}`, {
        method: "GET",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        setVariety(data.data || null);
      } else {
        setError(data.message || "Failed to load variety details");
        setVariety(null);
      }
    } catch (err) {
      setError("Something went wrong while loading this variety.");
      setVariety(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchVariety();
  }, [fetchVariety]);

  const mappedBuyerIds = new Set((variety?.buyers || []).map((b) => b._id));

  const loadAllBuyers = useCallback(async () => {
    try {
      setBuyersLoading(true);
      setBuyersError("");
      const res = await fetch(`${config.apiBaseUrl}/buyers/buyer/get`, {
        method: "GET",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        setAllBuyers(Array.isArray(data.data) ? data.data : []);
      } else {
        setAllBuyers([]);
        setBuyersError(data.message || "Failed to load buyers");
      }
    } catch (err) {
      setAllBuyers([]);
      setBuyersError("Something went wrong while loading buyers.");
    } finally {
      setBuyersLoading(false);
    }
  }, []);

  const loadMandiPartners = useCallback(async () => {
    try {
      setMandiPartnersLoading(true);
      setMandiPartnersError("");

      const limit = 100;
      let page = 1;
      let all = [];
      let total = Infinity;

      // Paginate through /mandi-partner/get-all until every record is loaded
      while (all.length < total) {
        const res = await fetch(
          `${config.apiBaseUrl}/mandi-partner/get-all?page=${page}&limit=${limit}`,
          { method: "GET", headers: authHeaders() }
        );
        const data = await res.json().catch(() => ({}));

        if (!res.ok || data.success === false) {
          throw new Error(data.message || "Failed to load mandi partners");
        }

        const batch = Array.isArray(data.data) ? data.data : [];
        all = all.concat(batch);
        total = typeof data.total === "number" ? data.total : all.length;

        if (batch.length === 0) break;
        page += 1;
      }

      setMandiPartners(all);
      setMandiPartnersLoaded(true);
    } catch (err) {
      setMandiPartners([]);
      setMandiPartnersError(err.message || "Something went wrong while loading mandi partners.");
    } finally {
      setMandiPartnersLoading(false);
    }
  }, []);

  const openMapDialog = () => {
    setSelectedBuyers([]);
    setBuyersError("");
    setMapDialogOpen(true);
    loadAllBuyers();
  };

  const closeMapDialog = () => {
    if (mapping) return;
    setMapDialogOpen(false);
    setSelectedBuyers([]);
  };

  const handleMapBuyers = async () => {
    if (selectedBuyers.length === 0 || !variety?._id) return;

    try {
      setMapping(true);

      const results = await Promise.allSettled(
        selectedBuyers.map(async (buyer) => {
          const res = await fetch(
            `${config.apiBaseUrl}/buyers/buyer/${buyer._id}/variety/${variety._id}`,
            { method: "POST", headers: authHeaders() }
          );
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data.success === false) {
            throw new Error(data.message || `Failed to map ${buyer.buyerName}`);
          }
          return buyer;
        })
      );

      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;

      if (succeeded > 0) {
        setSnackbar({
          open: true,
          message:
            failed === 0
              ? `${succeeded} buyer${succeeded > 1 ? "s" : ""} mapped to variety`
              : `${succeeded} mapped, ${failed} failed — try the rest again`,
          severity: failed === 0 ? "success" : "warning",
        });
        setMapDialogOpen(false);
        setSelectedBuyers([]);
        await fetchVariety();
      } else {
        setSnackbar({ open: true, message: "Failed to map buyers", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
    } finally {
      setMapping(false);
    }
  };

  const confirmRemoveBuyer = (buyer) => setRemoveDialogBuyer(buyer);

  const closeRemoveDialog = () => {
    if (removingId) return;
    setRemoveDialogBuyer(null);
  };

  const handleRemoveBuyer = async () => {
    if (!removeDialogBuyer || !variety?._id) return;

    try {
      setRemovingId(removeDialogBuyer._id);
      const res = await fetch(
        `${config.apiBaseUrl}/buyers/buyer/${removeDialogBuyer._id}/variety/${variety._id}`,
        {
          method: "DELETE",
          headers: authHeaders(),
        }
      );
      const data = await res.json().catch(() => ({}));

      setRemoveDialogBuyer(null);

      if (res.ok && data.success !== false) {
        setSnackbar({
          open: true,
          message: data.message || "Buyer removed from variety",
          severity: "success",
        });
        await fetchVariety();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to remove buyer",
          severity: "error",
        });
      }
    } catch (err) {
      setRemoveDialogBuyer(null);
      setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
    } finally {
      setRemovingId(null);
    }
  };

  const selectableBuyers = allBuyers.filter((b) => !mappedBuyerIds.has(b._id));

  const expectedArrivals = variety?.expectedArrivals || [];
  const totalExpectedQuantity = expectedArrivals.reduce(
    (sum, entry) => sum + (Number(entry.expectedArrival) || 0),
    0
  );
  const sortedArrivals = [...expectedArrivals]
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const yearDiff = (a.entry.year || 0) - (b.entry.year || 0);
      if (yearDiff !== 0) return yearDiff;
      return (SEASON_ORDER[a.entry.season] ?? 0) - (SEASON_ORDER[b.entry.season] ?? 0);
    });

  const mandiLocationOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    mandiPartners.forEach((p) => {
      if (p.mandiLocation && !seen.has(p.mandiLocation)) {
        seen.add(p.mandiLocation);
        options.push(p.mandiLocation);
      }
    });
    return options.sort((a, b) => a.localeCompare(b));
  }, [mandiPartners]);

  const mandiPartnerOptions = useMemo(() => {
    if (!arrivalMandiLocation) return mandiPartners;
    return mandiPartners.filter((p) => p.mandiLocation === arrivalMandiLocation);
  }, [mandiPartners, arrivalMandiLocation]);

  const handleArrivalMandiLocationChange = (newLocation) => {
    setArrivalMandiLocation(newLocation || "");
    if (newLocation) {
      // If exactly one partner exists at this location, prefill it automatically
      const matches = mandiPartners.filter((p) => p.mandiLocation === newLocation);
      setArrivalMandiPartner(matches.length === 1 ? matches[0] : null);
    } else {
      setArrivalMandiPartner(null);
    }
  };

  const handleArrivalMandiPartnerChange = (newPartner) => {
    setArrivalMandiPartner(newPartner);
    setArrivalMandiLocation(newPartner?.mandiLocation || "");
  };

  const openAddArrivalDialog = () => {
    setArrivalYear(String(currentYear));
    setArrivalSeason("");
    setArrivalQuantity("");
    setArrivalMandiLocation("");
    setArrivalMandiPartner(null);
    setArrivalFormError("");
    setArrivalDialogOpen(true);
    if (!mandiPartnersLoaded) loadMandiPartners();
  };

  const closeArrivalDialog = () => {
    if (savingArrival) return;
    setArrivalDialogOpen(false);
  };

  const persistVariety = async (overrides) => {
    const res = await fetch(`${config.apiBaseUrl}/variety/update/${variety._id}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({
        varietyName: variety.varietyName,
        varietyCode: variety.varietyCode,
        description: variety.description,
        status: variety.status,
        expectedArrivals: variety.expectedArrivals || [],
        ...overrides,
      }),
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  // const persistArrivals = (updatedArrivals) => persistVariety({ expectedArrivals: updatedArrivals });

  const openEditVarietyDialog = () => {
    setEditVarietyName(variety?.varietyName || "");
    setEditVarietyCode(variety?.varietyCode || "");
    setEditVarietyDescription(variety?.description || "");
    setEditVarietyStatus(variety?.status !== false);
    setEditVarietyError("");
    setEditVarietyDialogOpen(true);
  };

  const closeEditVarietyDialog = () => {
    if (savingVariety) return;
    setEditVarietyDialogOpen(false);
  };

  const handleSaveVarietyEdit = async () => {
    if (!variety?._id) return;

    if (!editVarietyName.trim()) {
      setEditVarietyError("Variety name is required.");
      return;
    }
    if (!editVarietyCode.trim()) {
      setEditVarietyError("Variety code is required.");
      return;
    }
    setEditVarietyError("");

    try {
      setSavingVariety(true);

      const { res, data } = await persistVariety({
        varietyName: editVarietyName.trim(),
        varietyCode: editVarietyCode.trim(),
        description: editVarietyDescription.trim(),
        status: editVarietyStatus,
      });

      if (res.ok && data.success !== false) {
        setSnackbar({ open: true, message: "Variety details updated", severity: "success" });
        setEditVarietyDialogOpen(false);
        await fetchVariety();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to update variety",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
    } finally {
      setSavingVariety(false);
    }
  };

  // Add: POST {url}/variety/:mandiPartnerId/:varietyId/expected-arrival
  const handleSaveArrival = async () => {
    if (!variety?._id) return;

    const yearNum = Number(arrivalYear);
    if (!arrivalYear || Number.isNaN(yearNum) || yearNum < 2000) {
      setArrivalFormError("Please enter a valid year.");
      return;
    }
    if (!arrivalSeason) {
      setArrivalFormError("Please select a season.");
      return;
    }
    if (!arrivalMandiLocation) {
      setArrivalFormError("Please select a mandi location.");
      return;
    }
    if (!arrivalMandiPartner) {
      setArrivalFormError("Please select a mandi partner.");
      return;
    }
    const qty = Number(arrivalQuantity);
    if (!arrivalQuantity || Number.isNaN(qty) || qty <= 0) {
      setArrivalFormError("Please enter a valid expected arrival quantity.");
      return;
    }

    const duplicateIndex = expectedArrivals.findIndex(
      (entry) => entry.year === yearNum && entry.season === arrivalSeason
    );
    if (duplicateIndex !== -1) {
      setArrivalFormError(
        `An entry for ${seasonLabel(arrivalSeason)} ${yearNum} already exists.`
      );
      return;
    }

    setArrivalFormError("");

    try {
      setSavingArrival(true);

      const res = await fetch(
        `${config.apiBaseUrl}/variety/${arrivalMandiPartner._id}/${variety._id}/expected-arrival`,
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            year: yearNum,
            season: arrivalSeason,
            expectedArrival: qty,
            mandiLocation: arrivalMandiLocation,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success !== false) {
        setSnackbar({
          open: true,
          message: "Expected arrival added",
          severity: "success",
        });
        setArrivalDialogOpen(false);
        await fetchVariety();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to save expected arrival",
          severity: "error",
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
    } finally {
      setSavingArrival(false);
    }
  };

  const confirmRemoveArrival = (entry, index) => {
    setRemoveArrivalTarget({ key: entry._id ?? index, index, entry });
  };

  const closeRemoveArrivalDialog = () => {
    if (removingArrivalKey !== null) return;
    setRemoveArrivalTarget(null);
  };

  const handleRemoveArrival = async () => {
    if (!removeArrivalTarget || !variety?._id) return;

    const mandiPartnerId = removeArrivalTarget.entry?.mandiPartner?._id;
    if (!mandiPartnerId) {
      setRemoveArrivalTarget(null);
      setSnackbar({
        open: true,
        message: "Missing mandi partner reference for this entry",
        severity: "error",
      });
      return;
    }

    try {
      setRemovingArrivalKey(removeArrivalTarget.key);

      const res = await fetch(
        `${config.apiBaseUrl}/variety/${mandiPartnerId}/${variety._id}/expected-arrival`,
        {
          method: "DELETE",
          headers: authHeaders(),
          body: JSON.stringify({
            year: removeArrivalTarget.entry?.year,
            season: removeArrivalTarget.entry?.season,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));

      setRemoveArrivalTarget(null);

      if (res.ok && data.success !== false) {
        setSnackbar({ open: true, message: "Expected arrival removed", severity: "success" });
        await fetchVariety();
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Failed to remove expected arrival",
          severity: "error",
        });
      }
    } catch (err) {
      setRemoveArrivalTarget(null);
      setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
    } finally {
      setRemovingArrivalKey(null);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", background: "#F8FAFC" }}>
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: "hidden" }}>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, minWidth: 0, width: "100%" }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
            {isMobile && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{ border: "1px solid #E2E8F0", borderRadius: "10px" }}
              >
                <MenuRoundedIcon />
              </IconButton>
            )}

            <IconButton
              onClick={() => navigate(-1)}
              sx={{ border: "1px solid #E2E8F0", borderRadius: "10px" }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>

            <Box>
              <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                Variety Details
              </Typography>
              {variety?.varietyName && (
                <Typography variant="body2" sx={{ color: "#64748B" }}>
                  {variety.varietyName}
                </Typography>
              )}
            </Box>

            {loading && <CircularProgress size={18} sx={{ ml: 1 }} />}
          </Stack>

          {!loading && error && (
            <Paper
              variant="outlined"
              sx={{ p: 4, borderRadius: 2, textAlign: "center", borderColor: "#E2E8F0" }}
            >
              <Typography color="error" fontWeight={600}>
                {error}
              </Typography>
              <Button
                onClick={fetchVariety}
                variant="outlined"
                sx={{ mt: 2, textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
              >
                Try again
              </Button>
            </Paper>
          )}

          {!loading && !error && variety && (
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: "#E2E8F0" }}>
                <Box sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    flexWrap="wrap"
                    rowGap={1}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {variety.varietyName || "—"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#64748B", mt: 0.25 }}>
                        Code: {variety.varietyCode || "—"}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        label={variety.status !== false ? "Active" : "Inactive"}
                        sx={{
                          fontWeight: 600,
                          bgcolor: variety.status !== false ? "#ECFDF5" : "#F1F5F9",
                          color: variety.status !== false ? "#047857" : "#475569",
                        }}
                      />

                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditRoundedIcon fontSize="small" />}
                        onClick={openEditVarietyDialog}
                        sx={{
                          borderRadius: "8px",
                          textTransform: "none",
                          fontWeight: 600,
                          borderColor: "#E2E8F0",
                          color: "#334155",
                          "&:hover": { borderColor: "#CBD5E1", bgcolor: "#F8FAFC" },
                        }}
                      >
                        Edit
                      </Button>
                    </Stack>
                  </Stack>

                  {variety.description && (
                    <Typography variant="body2" sx={{ mt: 2, color: "#334155" }}>
                      {variety.description}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: "#E2E8F0" }} />

                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    sx={{ p: 2.5, borderRight: { sm: "1px solid #E2E8F0" } }}
                  >
                    <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                      Created by
                    </Typography>
                    <Typography fontWeight={600}>{variety.createdBy?.name || "—"}</Typography>
                    <Typography variant="body2" sx={{ color: "#64748B" }}>
                      {variety.createdBy?.email || ""}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={6}
                    sm={3}
                    md={4}
                    sx={{ p: 2.5, borderRight: { sm: "1px solid #E2E8F0" } }}
                  >
                    <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                      Created on
                    </Typography>
                    <Typography fontWeight={600}>{formatDate(variety.createdAt)}</Typography>
                  </Grid>

                  <Grid item xs={6} sm={3} md={4} sx={{ p: 2.5 }}>
                    <Typography variant="caption" sx={{ color: "#94A3B8" }}>
                      Last updated
                    </Typography>
                    <Typography fontWeight={600}>{formatDate(variety.updatedAt)}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: "#E2E8F0" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  rowGap={1}
                  sx={{ p: 3, pb: 2 }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    Mapped Buyers
                    {typeof variety.buyerCount === "number" ? ` (${variety.buyerCount})` : ""}
                  </Typography>

                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<PersonAddAltRoundedIcon />}
                    onClick={openMapDialog}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: "#2563EB",
                      "&:hover": { bgcolor: "#1D4ED8" },
                    }}
                  >
                    Add Buyers
                  </Button>
                </Stack>

                <Divider sx={{ borderColor: "#E2E8F0" }} />

                {!variety.buyers || variety.buyers.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography sx={{ color: "#64748B" }}>
                      No buyers mapped to this variety yet.
                    </Typography>
                    <Button
                      onClick={openMapDialog}
                      sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}
                    >
                      Add your first buyer
                    </Button>
                  </Box>
                ) : (
                  <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                    <Table
                      sx={{
                        minWidth: 560,
                        borderCollapse: "collapse",
                        "& th, & td": {
                          border: "1px solid #E2E8F0",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: "#64748B", fontWeight: 600, bgcolor: "#F8FAFC" }}>
                            Buyer
                          </TableCell>
                          <TableCell sx={{ color: "#64748B", fontWeight: 600, bgcolor: "#F8FAFC" }}>
                            Contact number
                          </TableCell>
                          <TableCell sx={{ color: "#64748B", fontWeight: 600, bgcolor: "#F8FAFC" }}>
                            Email
                          </TableCell>
                          <TableCell sx={{ color: "#64748B", fontWeight: 600, bgcolor: "#F8FAFC" }} align="right">
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variety.buyers.map((buyer) => (
                          <TableRow key={buyer._id}>
                            <TableCell>
                              <Typography fontWeight={600}>{buyer.buyerName}</Typography>
                            </TableCell>
                            <TableCell sx={{ color: "#334155" }}>
                              {buyer.contactNumber || "—"}
                            </TableCell>
                            <TableCell sx={{ color: "#334155" }}>
                              {buyer.email || "—"}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => confirmRemoveBuyer(buyer)}
                                disabled={removingId === buyer._id}
                                sx={{ color: "#DC2626", "&:hover": { bgcolor: "rgba(220,38,38,0.08)" } }}
                              >
                                {removingId === buyer._id ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: "#E2E8F0", overflow: "hidden" }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  rowGap={1.5}
                  sx={{ p: 3, pb: 2 }}
                >
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#EFF6FF",
                          color: "#2563EB",
                        }}
                      >
                        <EventRepeatRoundedIcon fontSize="small" />
                      </Box>
                      <Typography variant="h6" fontWeight={700}>
                        Expected Arrival Quantity
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: "#64748B", mt: 0.5, ml: "42px" }}>
                      Plan how much stock is expected to arrive for each year and season.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    disableElevation
                    startIcon={<EventRepeatRoundedIcon fontSize="small" />}
                    onClick={openAddArrivalDialog}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: "#2563EB",
                      "&:hover": { bgcolor: "#1D4ED8" },
                    }}
                  >
                    Add Entry
                  </Button>
                </Stack>

                {expectedArrivals.length > 0 && (
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1} sx={{ px: 3, pb: 2.5 }}>
                    <Chip
                      size="small"
                      icon={<CalendarMonthRoundedIcon sx={{ fontSize: 16 }} />}
                      label={`${expectedArrivals.length} ${expectedArrivals.length === 1 ? "entry" : "entries"}`}
                      sx={{ fontWeight: 600, bgcolor: "#F1F5F9", color: "#334155" }}
                    />
                    <Chip
                      size="small"
                      icon={<Inventory2RoundedIcon sx={{ fontSize: 16 }} />}
                      label={`${formatQuantity(totalExpectedQuantity)} total expected qty`}
                      sx={{ fontWeight: 600, bgcolor: "#ECFDF5", color: "#047857" }}
                    />
                  </Stack>
                )}

                <Divider sx={{ borderColor: "#E2E8F0" }} />

                {expectedArrivals.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 6, px: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: "#F1F5F9",
                        color: "#94A3B8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                      }}
                    >
                      <EventRepeatRoundedIcon />
                    </Box>
                    <Typography sx={{ color: "#64748B" }}>
                      No expected arrival quantities added yet.
                    </Typography>
                    <Button
                      onClick={openAddArrivalDialog}
                      sx={{ mt: 1.5, textTransform: "none", fontWeight: 600 }}
                    >
                      Add your first entry
                    </Button>
                  </Box>
                ) : (
                  <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                    <Table
                      sx={{
                        minWidth: 640,
                        "& td, & th": { borderBottom: "1px solid #E2E8F0", borderTop: "none", borderLeft: "none", borderRight: "none" },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: "#94A3B8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, bgcolor: "#F8FAFC" }}>
                            Year
                          </TableCell>
                          <TableCell sx={{ color: "#94A3B8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, bgcolor: "#F8FAFC" }}>
                            Season
                          </TableCell>
                          <TableCell sx={{ color: "#94A3B8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, bgcolor: "#F8FAFC" }}>
                            Mandi Partner
                          </TableCell>
                          <TableCell sx={{ color: "#94A3B8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, bgcolor: "#F8FAFC" }} align="right">
                            Expected Arrival
                          </TableCell>
                          <TableCell sx={{ color: "#94A3B8", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4, bgcolor: "#F8FAFC", width: 96 }} align="right">
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedArrivals.map(({ entry, index }) => {
                          const rowKey = entry._id ?? index;
                          const isRemoving = removingArrivalKey === (entry._id ?? index);
                          return (
                            <TableRow
                              key={rowKey}
                              sx={{ "&:hover": { bgcolor: "#F8FAFC" }, transition: "background-color 0.15s" }}
                            >
                              <TableCell>
                                <Typography variant="body2" fontWeight={700} sx={{ color: "#1E293B" }}>
                                  {entry.year ?? "—"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={seasonLabel(entry.season)}
                                  sx={{ fontWeight: 600, bgcolor: "#F1F5F9", color: "#334155" }}
                                />
                              </TableCell>
                              <TableCell>
                                {entry.mandiPartner?.mandiPartner ? (
                                  <>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1E293B" }}>
                                      {entry.mandiPartner.mandiPartner}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                                      {entry.mandiLocation || entry.mandiPartner.mandiLocation || "—"}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography variant="body2" sx={{ color: "#94A3B8" }}>
                                    —
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  size="small"
                                  label={formatQuantity(entry.expectedArrival)}
                                  sx={{ fontWeight: 700, bgcolor: "#EFF6FF", color: "#1D4ED8" }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => confirmRemoveArrival(entry, index)}
                                  disabled={isRemoving}
                                  sx={{ color: "#DC2626", "&:hover": { bgcolor: "rgba(220,38,38,0.08)" } }}
                                >
                                  {isRemoving ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <DeleteOutlineRoundedIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={editVarietyDialogOpen}
        onClose={closeEditVarietyDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#EFF6FF",
                color: "#2563EB",
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Edit Variety Details
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {editVarietyError && (
            <Box
              sx={{
                bgcolor: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#B91C1C",
                borderRadius: "10px",
                px: 2,
                py: 1,
                mb: 2.5,
                mt: 1,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {editVarietyError}
              </Typography>
            </Box>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Variety name"
              fullWidth
              value={editVarietyName}
              onChange={(e) => setEditVarietyName(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            <TextField
              label="Variety code"
              fullWidth
              value={editVarietyCode}
              onChange={(e) => setEditVarietyCode(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={editVarietyDescription}
              onChange={(e) => setEditVarietyDescription(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #E2E8F0",
                borderRadius: "10px",
                px: 2,
                py: 1.25,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ color: "#1E293B" }}>
                  Status
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748B", display: { xs: "none", sm: "block" } }}
                >
                  {editVarietyStatus ? "Active — visible and usable across the app" : "Inactive — hidden from selection"}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={editVarietyStatus}
                    onChange={(e) => setEditVarietyStatus(e.target.checked)}
                  />
                }
                label={editVarietyStatus ? "Active" : "Inactive"}
                labelPlacement="start"
                sx={{ mr: 0, "& .MuiFormControlLabel-label": { fontWeight: 600, color: "#334155" } }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={closeEditVarietyDialog} disabled={savingVariety} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSaveVarietyEdit}
            disabled={savingVariety}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            {savingVariety ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={arrivalDialogOpen}
        onClose={closeArrivalDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#EFF6FF",
                color: "#2563EB",
              }}
            >
              <EventRepeatRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Add Expected Arrival Quantity
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 2.5 }}>
            Select the year and season, then enter the quantity you expect to arrive during that time period.
          </Typography>

          {arrivalFormError && (
            <Box
              sx={{
                bgcolor: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#B91C1C",
                borderRadius: "10px",
                px: 2,
                py: 1,
                mb: 2.5,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {arrivalFormError}
              </Typography>
            </Box>
          )}

          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Mandi Details
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1, mb: 1 }}>
            <Autocomplete
              fullWidth
              options={mandiLocationOptions}
              loading={mandiPartnersLoading}
              value={arrivalMandiLocation || null}
              onChange={(_, newValue) => handleArrivalMandiLocationChange(newValue)}
              noOptionsText={mandiPartnersLoading ? "Loading…" : "No mandi locations available"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mandi Location"
                  placeholder="Select location"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <LocationOnRoundedIcon sx={{ fontSize: 18, color: "#94A3B8", ml: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {mandiPartnersLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Autocomplete
              fullWidth
              loading={mandiPartnersLoading}
              options={mandiPartnerOptions}
              getOptionLabel={(option) => option.mandiPartner || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              value={arrivalMandiPartner}
              onChange={(_, newValue) => handleArrivalMandiPartnerChange(newValue)}
              noOptionsText={
                mandiPartnersLoading
                  ? "Loading…"
                  : arrivalMandiLocation
                  ? "No mandi partners for this location"
                  : "No mandi partners available"
              }
              renderOption={(props, option) => (
                <li {...props} key={option._id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.mandiPartner}
                    </Typography>
                    {(option.businessName || option.contactNumber) && (
                      <Typography variant="caption" sx={{ color: "#64748B" }}>
                        {[option.businessName, option.contactNumber].filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mandi Partner"
                  placeholder="Select mandi partner"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PersonRoundedIcon sx={{ fontSize: 18, color: "#94A3B8", ml: 0.5 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>

          {mandiPartnersError && (
            <Box
              sx={{
                bgcolor: "#FEF2F2",
                border: "1px solid #FECACA",
                color: "#B91C1C",
                borderRadius: "10px",
                px: 2,
                py: 1,
                mb: 2.5,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {mandiPartnersError}
              </Typography>
            </Box>
          )}

          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Time Period
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1, mb: 3 }}>
            <TextField
              label="Year"
              type="number"
              fullWidth
              value={arrivalYear}
              onChange={(e) => setArrivalYear(e.target.value)}
              inputProps={{ min: 2000, step: 1 }}
              InputProps={{
                startAdornment: <CalendarMonthRoundedIcon sx={{ fontSize: 18, color: "#94A3B8", mr: 1 }} />,
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              select
              label="Season"
              fullWidth
              value={arrivalSeason}
              onChange={(e) => setArrivalSeason(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            >
              {SEASON_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Expected Arrival Quantity
          </Typography>
          <TextField
            placeholder="e.g. 25000"
            type="number"
            fullWidth
            value={arrivalQuantity}
            onChange={(e) => setArrivalQuantity(e.target.value)}
            inputProps={{ min: 0, step: "any" }}
            InputProps={{
              startAdornment: <Inventory2RoundedIcon sx={{ fontSize: 18, color: "#94A3B8", mr: 1 }} />,
            }}
            sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button onClick={closeArrivalDialog} disabled={savingArrival} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSaveArrival}
            disabled={savingArrival}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            {savingArrival ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Save Entry"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removeArrivalTarget)}
        onClose={closeRemoveArrivalDialog}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle fontWeight={700}>Remove Expected Arrival</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Remove the expected arrival for{" "}
            <strong>
              {seasonLabel(removeArrivalTarget?.entry?.season)} {removeArrivalTarget?.entry?.year ?? ""}
            </strong>{" "}
            ({formatQuantity(removeArrivalTarget?.entry?.expectedArrival)} qty)?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeRemoveArrivalDialog}
            disabled={removingArrivalKey !== null}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={handleRemoveArrival}
            disabled={removingArrivalKey !== null}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {removingArrivalKey !== null ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mapDialogOpen} onClose={closeMapDialog} fullWidth maxWidth="sm">
        <DialogTitle fontWeight={700}>Add Buyers to Variety</DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 2 }}>
            Search and select as many buyers as you need — they'll all be mapped at once.
          </Typography>

          {buyersError && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {buyersError}
            </Typography>
          )}

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={selectableBuyers}
            loading={buyersLoading}
            getOptionLabel={(option) => option.buyerName || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            value={selectedBuyers}
            onChange={(_, newValue) => setSelectedBuyers(newValue)}
            noOptionsText={buyersLoading ? "Loading…" : "No unmapped buyers available"}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option._id}>
                <Checkbox
                  icon={checkboxIcon}
                  checkedIcon={checkboxCheckedIcon}
                  checked={selected}
                  size="small"
                  sx={{ mr: 1, p: 0.5 }}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.buyerName}
                  </Typography>
                  {(option.contactNumber || option.email) && (
                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                      {[option.contactNumber, option.email].filter(Boolean).join(" · ")}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option._id}
                  label={option.buyerName}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select buyers"
                placeholder={selectedBuyers.length ? "" : "Search buyers…"}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {buyersLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeMapDialog} disabled={mapping} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleMapBuyers}
            disabled={selectedBuyers.length === 0 || mapping}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              bgcolor: "#2563EB",
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            {mapping ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : selectedBuyers.length > 1 ? (
              `Add ${selectedBuyers.length} Buyers`
            ) : (
              "Add Buyer"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removeDialogBuyer)} onClose={closeRemoveDialog}>
        <DialogTitle>Remove Buyer</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{" "}
            <strong>{removeDialogBuyer?.buyerName}</strong> from this variety?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeRemoveDialog} disabled={Boolean(removingId)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={handleRemoveBuyer}
            disabled={Boolean(removingId)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {removingId ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}